import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  DragIndicator,
  Preview,
} from "@mui/icons-material";
import {
  MatchingConfig,
  MatchingQuestion,
  MatchingQuestionType,
} from "../../types";

// ── Question type metadata ─────────────────────────────────────────────────────

const QUESTION_TYPE_LABELS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]: "Time Availability",
  [MatchingQuestionType.RolePreference]: "Role Preference",
  [MatchingQuestionType.Personality]: "Personality Type",
  [MatchingQuestionType.PreferredTeammates]: "Preferred Teammates",
  [MatchingQuestionType.AvoidTeammates]: "Avoid Teammates",
  [MatchingQuestionType.CustomText]: "Custom Question",
};

const QUESTION_TYPE_SUBLABELS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]:
    "Students pick available day/time slots from a 7×3 weekly grid",
  [MatchingQuestionType.RolePreference]:
    "Students select their preferred team role from a predefined list",
  [MatchingQuestionType.Personality]:
    "Students identify their MBTI personality type",
  [MatchingQuestionType.PreferredTeammates]:
    "Students list specific classmates they'd like to work with",
  [MatchingQuestionType.AvoidTeammates]:
    "Students list specific classmates they'd prefer not to work with",
  [MatchingQuestionType.CustomText]:
    "A single-select multiple-choice question you write yourself",
};

/** Chip colour per question type — makes chip visually distinct with good contrast */
const QUESTION_TYPE_CHIP_COLOR: Record<
  MatchingQuestionType,
  "default" | "error" | "info" | "secondary" | "success" | "warning"
> = {
  [MatchingQuestionType.TimeAvailability]: "info",
  [MatchingQuestionType.RolePreference]: "success",
  [MatchingQuestionType.Personality]: "secondary",
  [MatchingQuestionType.PreferredTeammates]: "warning",
  [MatchingQuestionType.AvoidTeammates]: "error",
  [MatchingQuestionType.CustomText]: "default",
};

/** Default affinity direction per question type */
const QUESTION_TYPE_DEFAULT_AFFINITY: Record<MatchingQuestionType, number> = {
  [MatchingQuestionType.TimeAvailability]: 1.0, // similarity — same schedule
  [MatchingQuestionType.RolePreference]: -1.0, // diversity — complementary roles
  [MatchingQuestionType.Personality]: -0.5, // lean diverse
  [MatchingQuestionType.PreferredTeammates]: 1.0, // similarity — honor preferences
  [MatchingQuestionType.AvoidTeammates]: -1.0, // anti-affinity — keep apart
  [MatchingQuestionType.CustomText]: 0.0, // neutral
};

/** Question types where the affinity slider should be hidden */
const HIDE_AFFINITY_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.TimeAvailability,
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

/** Question types that are always optional — students may skip without answering */
const ALWAYS_OPTIONAL_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

/** Maximum number of matching criteria (questions) per config */
const MAX_CRITERIA = 4;

// Standard types that should only appear once
const SINGLETON_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.TimeAvailability,
  MatchingQuestionType.RolePreference,
  MatchingQuestionType.Personality,
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

const DEFAULT_LABELS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]: "When are you available to meet?",
  [MatchingQuestionType.RolePreference]: "What role do you prefer?",
  [MatchingQuestionType.Personality]: "What is your personality type?",
  [MatchingQuestionType.PreferredTeammates]:
    "Are there classmates you'd like to work with?",
  [MatchingQuestionType.AvoidTeammates]:
    "Are there classmates you'd prefer not to work with?",
  [MatchingQuestionType.CustomText]: "New question",
};

const DEFAULT_DESCRIPTIONS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]:
    "Select all time slots that work for you.",
  [MatchingQuestionType.RolePreference]:
    "Select the role you would most like to take on.",
  [MatchingQuestionType.Personality]:
    "Select the option that best describes you.",
  [MatchingQuestionType.PreferredTeammates]: "",
  [MatchingQuestionType.AvoidTeammates]: "",
  [MatchingQuestionType.CustomText]: "",
};

/** Types where the question label is preset and should not be editable by the instructor */
const PRESET_LABEL_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.TimeAvailability,
  MatchingQuestionType.RolePreference,
  MatchingQuestionType.Personality,
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

/** Default role options for the Role Preference question (up to 5) */
const DEFAULT_ROLE_OPTIONS = [
  "Project Manager",
  "Designer",
  "Developer",
  "Researcher",
  "Presenter",
];

const MAX_ROLE_OPTIONS = 5;

// ── Survey preview ─────────────────────────────────────────────────────────────

const PREVIEW_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PREVIEW_SLOTS = ["Morning", "Afternoon", "Evening"];

function SurveyPreview({
  questions,
}: { questions: Partial<MatchingQuestion>[] }) {
  return (
    <Box>
      {questions.map((q, i) => {
        const roleOptions =
          (q.options as string[] | undefined)?.length
            ? (q.options as string[])
            : DEFAULT_ROLE_OPTIONS;
        const customOptions = (q.options as string[] | undefined) ?? [];
        return (
          <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography fontWeight={600} mb={0.5}>
              {q.label || "(no label)"}
              {q.required && (
                <Typography component="span" color="error" ml={0.5}>
                  *
                </Typography>
              )}
            </Typography>
            {q.description && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {q.description}
              </Typography>
            )}
            {/* Show a representative sample of each input type */}
            {q.questionType === MatchingQuestionType.TimeAvailability && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${PREVIEW_DAYS.length}, 1fr)`,
                  gap: 0.5,
                }}
              >
                {PREVIEW_DAYS.map((d) => (
                  <Typography
                    key={d}
                    variant="caption"
                    align="center"
                    fontWeight={600}
                    sx={{ py: 0.5 }}
                  >
                    {d}
                  </Typography>
                ))}
                {PREVIEW_SLOTS.map((slot) =>
                  PREVIEW_DAYS.map((d) => (
                    <Chip
                      key={`${d}-${slot}`}
                      label={slot.substring(0, 2)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.65rem", height: 22 }}
                    />
                  )),
                )}
              </Box>
            )}
            {q.questionType === MatchingQuestionType.RolePreference && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {roleOptions.map((r) => (
                  <Chip key={r} label={r} variant="outlined" size="small" />
                ))}
              </Box>
            )}
            {q.questionType === MatchingQuestionType.Personality && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {["INTJ", "INTP", "ENTJ", "ENTP", "INFJ", "INFP"].map((t) => (
                  <Chip key={t} label={t} variant="outlined" size="small" />
                ))}
                <Typography variant="caption" color="text.secondary">
                  …and more
                </Typography>
              </Box>
            )}
            {(q.questionType === MatchingQuestionType.PreferredTeammates ||
              q.questionType === MatchingQuestionType.AvoidTeammates) && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Search classmates by name (optional)
              </Typography>
            )}
            {q.questionType === MatchingQuestionType.CustomText && (
              customOptions.length > 0 ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {customOptions.map((opt) => (
                    <Chip key={opt} label={opt} variant="outlined" size="small" />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  (no options yet)
                </Typography>
              )
            )}
          </Paper>
        );
      })}
    </Box>
  );
}

// ── Draft types ────────────────────────────────────────────────────────────────

type DraftConfig = {
  name: string;
  description: string;
  isTemplate: boolean;
  questions: Partial<MatchingQuestion>[];
};

function defaultConfig(): DraftConfig {
  return {
    name: "Team Matching Config",
    description: "",
    isTemplate: false,
    questions: [
      {
        questionType: MatchingQuestionType.TimeAvailability,
        label: DEFAULT_LABELS[MatchingQuestionType.TimeAvailability],
        description:
          DEFAULT_DESCRIPTIONS[MatchingQuestionType.TimeAvailability],
        weight: 7,
        order: 0,
        options: [],
        required: true,
        similarityPreference:
          QUESTION_TYPE_DEFAULT_AFFINITY[MatchingQuestionType.TimeAvailability],
      },
      {
        questionType: MatchingQuestionType.RolePreference,
        label: DEFAULT_LABELS[MatchingQuestionType.RolePreference],
        description: DEFAULT_DESCRIPTIONS[MatchingQuestionType.RolePreference],
        weight: 5,
        order: 1,
        options: DEFAULT_ROLE_OPTIONS,
        required: true,
        similarityPreference:
          QUESTION_TYPE_DEFAULT_AFFINITY[MatchingQuestionType.RolePreference],
      },
    ],
  };
}

function configFromExisting(config: MatchingConfig): DraftConfig {
  return {
    name: config.name,
    description: config.description,
    isTemplate: config.isTemplate,
    questions: config.questions.map((q) => ({
      ...q,
      // Normalize: teammate questions are always optional regardless of DB value
      required: ALWAYS_OPTIONAL_TYPES.has(q.questionType as MatchingQuestionType)
        ? false
        : q.required,
    })),
  };
}

// ── Affinity slider label ──────────────────────────────────────────────────────

function affinityLabel(val: number): string {
  if (val <= -0.75) return "Maximize diversity";
  if (val <= -0.25) return "Prefer diverse";
  if (val < 0.25) return "Balanced";
  if (val < 0.75) return "Prefer similar";
  return "Maximize similarity";
}

// ── Props & component ──────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  existing?: MatchingConfig;
  onClose: () => void;
  onSave: (draft: DraftConfig) => void;
  saving?: boolean;
}

export function MatchingConfigEditor({
  open,
  existing,
  onClose,
  onSave,
  saving,
}: Props) {
  const [draft, setDraft] = useState<DraftConfig>(
    existing ? configFromExisting(existing) : defaultConfig(),
  );
  const [addPopoverAnchor, setAddPopoverAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  // DnD state
  const dragIndex = useRef<number | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(existing ? configFromExisting(existing) : defaultConfig());
    }
  }, [open, existing]);

  const set = <K extends keyof DraftConfig>(key: K, value: DraftConfig[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const setQuestion = (i: number, patch: Partial<MatchingQuestion>) =>
    setDraft((d) => {
      const questions = [...d.questions];
      questions[i] = { ...questions[i], ...patch };
      return { ...d, questions };
    });

  const addQuestion = (type: MatchingQuestionType) => {
    setAddPopoverAnchor(null);
    setDraft((d) => ({
      ...d,
      questions: [
        ...d.questions,
        {
          questionType: type,
          label: DEFAULT_LABELS[type],
          description: DEFAULT_DESCRIPTIONS[type],
          weight: 5,
          order: d.questions.length,
          options:
            type === MatchingQuestionType.RolePreference
              ? DEFAULT_ROLE_OPTIONS
              : [],
          required: !ALWAYS_OPTIONAL_TYPES.has(type),
          similarityPreference: QUESTION_TYPE_DEFAULT_AFFINITY[type],
        },
      ],
    }));
  };

  const removeQuestion = (i: number) =>
    setDraft((d) => ({
      ...d,
      questions: d.questions.filter((_, idx) => idx !== i),
    }));

  const moveQuestion = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const questions = [...d.questions];
      const target = i + dir;
      if (target < 0 || target >= questions.length) return d;
      [questions[i], questions[target]] = [questions[target], questions[i]];
      return { ...d, questions };
    });

  // Which singleton types are already in the config?
  const usedSingletons = new Set(
    draft.questions
      .map((q) => q.questionType as MatchingQuestionType)
      .filter((t) => SINGLETON_TYPES.has(t)),
  );

  // Available types for the Add popover (hide already-used singletons)
  const availableTypes = Object.values(MatchingQuestionType).filter(
    (t) => !SINGLETON_TYPES.has(t) || !usedSingletons.has(t),
  );

  // HTML5 DnD handlers
  const handleDragStart = (i: number) => {
    dragIndex.current = i;
  };

  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setDraft((d) => {
      const questions = [...d.questions];
      const [removed] = questions.splice(from, 1);
      questions.splice(i, 0, removed);
      dragIndex.current = i;
      return { ...d, questions };
    });
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {existing ? "Edit Matching Preferences" : "New Matching Preferences"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Config name + template toggle — inline */}
            <Box display="flex" alignItems="flex-start" gap={2}>
              <TextField
                label="Preferences Name"
                fullWidth
                value={draft.name}
                onChange={(e) => set("name", e.target.value)}
                required
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.isTemplate}
                    onChange={(e) => set("isTemplate", e.target.checked)}
                  />
                }
                label="Save as reusable template"
                sx={{ whiteSpace: "nowrap", mt: 1 }}
              />
            </Box>

            {/* Description — only shown when saving as template */}
            {draft.isTemplate && (
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={draft.description}
                onChange={(e) => set("description", e.target.value)}
                helperText="Describe when to use this template so you can find it later."
              />
            )}

            {/* Survey questions */}
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Box>
                  <Typography variant="subtitle2">Survey Questions</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Drag to reorder. Set each question&apos;s team affinity
                    (diverse vs similar) individually.
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Preview />}
                    onClick={() => setPreviewOpen(true)}
                    disabled={draft.questions.length === 0}
                  >
                    Preview Survey
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={(e) => setAddPopoverAnchor(e.currentTarget)}
                    disabled={
                      availableTypes.length === 0 ||
                      draft.questions.length >= MAX_CRITERIA
                    }
                  >
                    Add Question
                  </Button>
                  {draft.questions.length >= MAX_CRITERIA && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      Maximum 4 criteria
                    </Typography>
                  )}
                </Box>
              </Box>

              {draft.questions.length === 0 && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  align="center"
                  py={3}
                >
                  No questions yet. Add at least one question.
                </Typography>
              )}

              {draft.questions.map((q, i) => {
                const qType = (q.questionType ??
                  MatchingQuestionType.CustomText) as MatchingQuestionType;
                const chipColor = QUESTION_TYPE_CHIP_COLOR[qType];
                const affinity = q.similarityPreference ?? 0;
                return (
                  <Box
                    key={i}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 1,
                      cursor: "grab",
                      "&:active": { cursor: "grabbing" },
                      bgcolor: "background.paper",
                      overflow: "hidden",
                    }}
                  >
                    {/* ── Header row: drag handle, type badge, reorder arrows, delete ── */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: "action.hover",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <DragIndicator
                        fontSize="small"
                        sx={{ color: "text.disabled", flexShrink: 0 }}
                      />
                      <Chip
                        label={QUESTION_TYPE_LABELS[qType]}
                        size="small"
                        color={chipColor}
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexGrow: 1, ml: 0.5 }}
                      >
                        {QUESTION_TYPE_SUBLABELS[qType]}
                      </Typography>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Move up"
                            disabled={i === 0}
                            onClick={() => moveQuestion(i, -1)}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Move down"
                            disabled={i === draft.questions.length - 1}
                            onClick={() => moveQuestion(i, 1)}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove question">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Delete question"
                          onClick={() => removeQuestion(i)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* ── Body: label, description, options, affinity ── */}
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        {/* Question label — static for preset types, editable for custom */}
                        {PRESET_LABEL_TYPES.has(qType) ? (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Question Label
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.25 }}>
                              {q.label}
                            </Typography>
                          </Box>
                        ) : (
                          <TextField
                            size="small"
                            label="Question Label"
                            fullWidth
                            value={q.label ?? ""}
                            onChange={(e) =>
                              setQuestion(i, { label: e.target.value })
                            }
                          />
                        )}

                        {/* Description */}
                        <TextField
                          size="small"
                          label="Description (optional)"
                          fullWidth
                          value={q.description ?? ""}
                          onChange={(e) =>
                            setQuestion(i, { description: e.target.value })
                          }
                        />

                        {/* Role options for RolePreference — editable chips (up to 5) */}
                        {qType === MatchingQuestionType.RolePreference && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Role options (up to {MAX_ROLE_OPTIONS})
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                              {(q.options as string[] ?? DEFAULT_ROLE_OPTIONS).map(
                                (role: string) => (
                                  <Chip
                                    key={role}
                                    label={role}
                                    size="small"
                                    onDelete={() => {
                                      const current = (q.options as string[]) ?? DEFAULT_ROLE_OPTIONS;
                                      setQuestion(i, { options: current.filter((r) => r !== role) });
                                    }}
                                  />
                                ),
                              )}
                            </Box>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Add />}
                              aria-label="add role"
                              disabled={
                                ((q.options as string[]) ?? DEFAULT_ROLE_OPTIONS).length >= MAX_ROLE_OPTIONS
                              }
                              onClick={() => {
                                const newRole = prompt("Enter role name:");
                                if (newRole?.trim()) {
                                  const current = (q.options as string[]) ?? DEFAULT_ROLE_OPTIONS;
                                  if (current.length < MAX_ROLE_OPTIONS) {
                                    setQuestion(i, { options: [...current, newRole.trim()] });
                                  }
                                }
                              }}
                            >
                              Add role
                            </Button>
                          </Box>
                        )}

                        {/* Options for CustomText — single-select choices */}
                        {qType === MatchingQuestionType.CustomText && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Answer choices (students pick one)
                            </Typography>
                            {((q.options as string[]) ?? []).map(
                              (opt: string, oi: number) => (
                                <Box key={oi} display="flex" alignItems="center" gap={1} mb={0.5}>
                                  <TextField
                                    size="small"
                                    value={opt}
                                    onChange={(e) => {
                                      const current = [...((q.options as string[]) ?? [])];
                                      current[oi] = e.target.value;
                                      setQuestion(i, { options: current });
                                    }}
                                    sx={{ flexGrow: 1 }}
                                    placeholder={`Option ${oi + 1}`}
                                    inputProps={{ "aria-label": `option ${oi + 1}` }}
                                  />
                                  <IconButton
                                    size="small"
                                    aria-label={`remove option ${oi + 1}`}
                                    onClick={() => {
                                      const current = [...((q.options as string[]) ?? [])];
                                      current.splice(oi, 1);
                                      setQuestion(i, { options: current });
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              ),
                            )}
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Add />}
                              aria-label="add option"
                              onClick={() => {
                                const current = (q.options as string[]) ?? [];
                                setQuestion(i, { options: [...current, ""] });
                              }}
                            >
                              Add option
                            </Button>
                          </Box>
                        )}

                        {/* Per-question affinity slider (hidden for time, preferred/avoid teammates) */}
                        {!HIDE_AFFINITY_TYPES.has(qType) && (
                            <Box>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                mb={0}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Team affinity
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {affinityLabel(affinity)}
                                  {affinity === 0 && (
                                    <Tooltip title="Balanced means this criterion has no effect on matching. Move the slider off center to use it.">
                                      <span style={{ marginLeft: 4, cursor: "help" }}>ⓘ</span>
                                    </Tooltip>
                                  )}
                                </Typography>
                              </Box>
                              <Slider
                                value={affinity}
                                min={-1}
                                max={1}
                                step={0.25}
                                marks={[
                                  { value: -1, label: "Diverse" },
                                  { value: 0, label: "Balanced" },
                                  { value: 1, label: "Similar" },
                                ]}
                                onChange={(_e, v) =>
                                  setQuestion(i, {
                                    similarityPreference: v as number,
                                  })
                                }
                                size="small"
                                aria-label={`team affinity for question ${
                                  i + 1
                                }`}
                                sx={{
                                  // Prevent edge mark labels from being clipped
                                  // by the parent card's overflow:hidden
                                  "& .MuiSlider-markLabel[data-index='0']": {
                                    transform: "translateX(0%)",
                                  },
                                  "& .MuiSlider-markLabel[data-index='2']": {
                                    transform: "translateX(-100%)",
                                  },
                                }}
                              />
                            </Box>
                          )}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => onSave(draft)}
            disabled={!draft.name || draft.questions.length === 0 || saving}
          >
            {saving ? "Saving…" : "Save Preferences"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Question popover */}
      <Popover
        open={Boolean(addPopoverAnchor)}
        anchorEl={addPopoverAnchor}
        onClose={() => setAddPopoverAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box p={1} minWidth={260}>
          <Typography
            variant="caption"
            color="text.secondary"
            px={1}
            display="block"
            mb={0.5}
          >
            Choose a question type
          </Typography>
          {availableTypes.map((type) => (
            <Box
              key={type}
              onClick={() => addQuestion(type)}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {QUESTION_TYPE_LABELS[type]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {QUESTION_TYPE_SUBLABELS[type]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Survey preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Survey Preview</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This is what students will see when they receive the survey.
          </Typography>
          <SurveyPreview questions={draft.questions} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export type { DraftConfig };
